import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import OtpInput from "react-otp-input";
import { Trans, useLingui } from "@lingui/macro";
import { type UserConnexionResponse } from "@api/src/types/responses";
import useUser from "@app/zustand/user";
import API from "@app/services/api";
import PscLoginButton from "@app/components/PscLoginButton";

/**
 * The steps of a login.
 *
 * - `password`: the primary factor of accounts that have one (no code is emailed
 *   to them).
 * - `psc`: accounts bound to an e-CPS and holding no password. The emailed code is
 *   closed to them — their way in is Pro Santé Connect.
 * - `otp`: the primary factor of accounts without a password (code received by email).
 * - `totp`: the second factor, when application MFA is enabled.
 * - `reset-otp` / `reset-password`: the forgotten-password reset. It proves access
 *   to the mailbox, never possession of the second factor, so the `totp` step still
 *   applies afterwards.
 */
type Step =
  | "password"
  | "psc"
  | "otp"
  | "totp"
  | "reset-otp"
  | "reset-password";

const PASSWORD_INPUT_CLASS =
  "w-full max-w-[500px] rounded border border-primary border-opacity-30 px-3 py-2 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-30";

// Shared 6-box OTP input rendering, reused for the email code and the TOTP code.
function renderOtpBoxes(
  value: string,
  onChange: (v: string) => void,
): React.ReactNode {
  return (
    <OtpInput
      value={value}
      onChange={onChange}
      numInputs={6}
      renderSeparator={(index) => {
        if (index === 2) return <span className="mx-2">-</span>;
        return null;
      }}
      renderInput={(props, index) => {
        return (
          <input
            {...props}
            inputMode="text" // hydration error without this
            maxLength={undefined} // hydration error without this
            className={[
              "inline-flex h-24 w-0 shrink grow basis-0 rounded-none border text-center text-[2rem] font-medium focus:outline-none focus:ring-2 focus:ring-opacity-30",
              index === 0 && "!rounded-l-md border-r-0",
              index === 1 && "",
              index === 2 && "!rounded-r-md border-l-0",
              index === 3 && "!rounded-l-md border-r-0",
              index === 4 && "",
              index === 5 && "!rounded-r-md border-l-0",
            ]
              .filter(Boolean)
              .join(" ")}
            style={{
              ...props.style,
              borderColor: "hsl(217 91% 60% / 0.7)",
              outline: "none",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "hsl(217 91% 60%)";
              e.target.style.boxShadow = "0 0 0 2px hsl(217 91% 60% / 0.3)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "hsl(217 91% 60% / 0.7)";
              e.target.style.boxShadow = "none";
            }}
          />
        );
      }}
    />
  );
}

export default function WelcomeLoginRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLingui();
  const setUser = useUser((state) => state.setUser);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") ?? "";

  // The login page names the entry step: `?step=password` for an account that has
  // one (in the URL, so it survives a page refresh), or an `mfaToken` in navigation
  // state for the development shortcut that skips the primary factor (never in the
  // URL).
  const navState = location.state as { mfaToken?: string } | null;
  const [mfaToken, setMfaToken] = useState<string | null>(
    navState?.mfaToken ?? null,
  );
  const [step, setStep] = useState<Step>(() => {
    if (navState?.mfaToken) return "totp";
    const requested = searchParams.get("step");
    if (requested === "password") return "password";
    if (requested === "psc") return "psc";
    return "otp";
  });
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [resetOtp, setResetOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  /** What follows a successful authentication: a session, or a second factor to enter. */
  function handleAuthResponse(response: UserConnexionResponse): void {
    if (response.error) {
      setError(response.error);
      return;
    }
    if (response.data?.mfa_required && response.data.mfa_token) {
      setMfaToken(response.data.mfa_token);
      setStep("totp");
      setError("");
      setNotice("");
      return;
    }
    if (response.ok && response.data?.user) {
      // Includes ``setup_required``: the session handed over only reaches the
      // enrollment endpoints, and RestrictedRoute renders the wizard from the
      // account's own ``auth`` block rather than from anything decided here.
      setUser(response.data.user);
      navigate(`/`);
    }
  }

  function onSubmitPassword() {
    if (!password) return;
    setLoading(true);
    setError("");
    API.post({ path: "/user/password/login", body: { email, password } })
      .then((response: UserConnexionResponse) => {
        if (response.error) setPassword("");
        handleAuthResponse(response);
      })
      .finally(() => setLoading(false));
  }

  function onSubmit(otp: string) {
    if (otp.length === 6) {
      setLoading(true);
      setError("");
      API.post({ path: "/user/magic-otp", body: { otp, email } })
        .then(handleAuthResponse)
        .finally(() => {
          setLoading(false);
        });
    }
  }

  function onSubmitTotp(code: string) {
    if (code.length === 6 && mfaToken) {
      setLoading(true);
      setError("");
      API.post({
        path: "/user/totp/verify-login",
        body: { mfa_token: mfaToken, code },
      })
        .then((response: UserConnexionResponse) => {
          if (response.error) setTotpCode("");
          handleAuthResponse(response);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }

  function onRequestReset() {
    setLoading(true);
    setError("");
    API.post({ path: "/user/password/reset-request", body: { email } })
      .then((response: UserConnexionResponse) => {
        if (response.error) {
          setError(response.error);
          return;
        }
        setNotice(t`Un code de vérification vous a été envoyé par email.`);
        setStep("reset-otp");
      })
      .finally(() => setLoading(false));
  }

  function onSubmitReset() {
    if (newPassword !== confirmPassword) {
      setError(t`Les deux mots de passe ne sont pas identiques.`);
      return;
    }
    setLoading(true);
    setError("");
    API.post({
      path: "/user/password/reset",
      body: { email, otp: resetOtp, new_password: newPassword },
    })
      .then(handleAuthResponse)
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    onSubmit(otp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  useEffect(() => {
    onSubmitTotp(totpCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totpCode]);

  const feedback = (
    <>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      {!error && notice && (
        <p className="mt-4 text-sm text-primary">{notice}</p>
      )}
    </>
  );

  // Step 1c: the e-CPS card (accounts bound to one and holding no password). Nothing
  // is typed here — the validation happens on the card or on the phone.
  if (step === "psc") {
    return (
      <>
        <h3 className="mb-2 text-lg font-semibold text-slate-900">
          <Trans>Connexion par carte CPS</Trans>
        </h3>
        <p className="text-slate-600">
          <Trans>
            Le compte <b className="text-primary">{email}</b> se connecte avec
            sa carte CPS ou son application e-CPS. Aucun code n'a été envoyé par
            email.
          </Trans>
        </p>
        <div className="mr-auto mt-4">
          <PscLoginButton />
        </div>
        <p className="mt-12 text-sm font-semibold italic text-slate-600">
          <Trans>
            Vous avez perdu l'accès à votre carte ? Contactez l'équipe MAGE-X
            pour rétablir un mot de passe.
          </Trans>
        </p>
      </>
    );
  }

  // Step 1a: the password (accounts that have one — no email is sent).
  if (step === "password") {
    return (
      <>
        <h3 className="mb-2 text-lg font-semibold text-slate-900">
          <Trans>Votre mot de passe</Trans>
        </h3>
        <p className="text-slate-600">
          <Trans>Connexion de</Trans> <b className="text-primary">{email}</b>
        </p>
        <form
          className="mt-8 flex flex-col items-start gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmitPassword();
          }}
        >
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            autoFocus
            aria-label={t`Mot de passe`}
            placeholder={t`Mot de passe`}
            className={PASSWORD_INPUT_CLASS}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            className="mr-auto inline-block rounded-lg border-none bg-primary px-10 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
            type="submit"
            disabled={loading || !password}
          >
            {loading ? (
              <Trans>Connexion...</Trans>
            ) : (
              <Trans>Se connecter</Trans>
            )}
          </button>
        </form>
        {feedback}
        <button
          type="button"
          onClick={onRequestReset}
          disabled={loading}
          className="mt-8 text-sm text-primary underline underline-offset-2 disabled:opacity-50"
        >
          <Trans>Mot de passe oublié ?</Trans>
        </button>
      </>
    );
  }

  // Step 2: application MFA, once the primary factor has passed.
  if (step === "totp") {
    return (
      <>
        <h3 className="mb-2 text-lg font-semibold text-slate-900">
          <Trans>Double authentification</Trans>
        </h3>
        <p className="text-slate-600">
          <Trans>
            Saisissez le code à 6 chiffres affiché par votre application
            d'authentification.
          </Trans>
        </p>
        <div className="-mx-4 mt-12 flex w-screen items-start justify-start xs:mx-0 xs:w-full ">
          <div className="w-full max-w-[500px] overflow-hidden [&>div]:w-full [&>div]:justify-start">
            {renderOtpBoxes(totpCode, setTotpCode)}
          </div>
        </div>
        <button
          className="mr-auto mt-5 inline-block rounded-lg border-none bg-primary px-10 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
          type="submit"
          onClick={() => {
            if (totpCode.length < 6) {
              setError(t`Veuillez entrer le code complet`);
              return;
            }
            onSubmitTotp(totpCode);
          }}
          disabled={loading}
        >
          {loading ? <Trans>Connexion...</Trans> : <Trans>Se connecter</Trans>}
        </button>
        {feedback}
        <p className="mt-12 text-sm font-semibold italic text-slate-600">
          <Trans>
            Le code change toutes les 30 secondes dans votre application.
          </Trans>
        </p>
      </>
    );
  }

  // Reset, step 1: the code received by email.
  if (step === "reset-otp") {
    return (
      <>
        <h3 className="mb-2 text-lg font-semibold text-slate-900">
          <Trans>Réinitialiser le mot de passe</Trans>
        </h3>
        <p className="text-slate-600">
          <Trans>Veuillez saisir le code envoyé à</Trans>{" "}
          <b className="text-primary">{email}</b>
        </p>
        <div className="-mx-4 mt-12 flex w-screen items-start justify-start xs:mx-0 xs:w-full ">
          <div className="w-full max-w-[500px] overflow-hidden [&>div]:w-full [&>div]:justify-start">
            {renderOtpBoxes(resetOtp, setResetOtp)}
          </div>
        </div>
        <button
          className="mr-auto mt-5 inline-block rounded-lg border-none bg-primary px-10 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
          type="button"
          onClick={() => {
            setError("");
            setNotice("");
            setStep("reset-password");
          }}
          disabled={loading || resetOtp.length < 6}
        >
          <Trans>Continuer</Trans>
        </button>
        {feedback}
      </>
    );
  }

  // Reset, step 2: the new password.
  if (step === "reset-password") {
    return (
      <>
        <h3 className="mb-2 text-lg font-semibold text-slate-900">
          <Trans>Nouveau mot de passe</Trans>
        </h3>
        <p className="text-slate-600">
          <Trans>
            Au moins 12 caractères, combinant au moins trois types parmi :
            minuscules, majuscules, chiffres, caractères spéciaux.
          </Trans>
        </p>
        <form
          className="mt-8 flex flex-col items-start gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmitReset();
          }}
        >
          <input
            type="password"
            name="new-password"
            autoComplete="new-password"
            autoFocus
            aria-label={t`Nouveau mot de passe`}
            placeholder={t`Nouveau mot de passe`}
            className={PASSWORD_INPUT_CLASS}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <input
            type="password"
            name="confirm-password"
            autoComplete="new-password"
            aria-label={t`Confirmer le mot de passe`}
            placeholder={t`Confirmer le mot de passe`}
            className={PASSWORD_INPUT_CLASS}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <div className="flex flex-wrap gap-3">
            <button
              className="inline-block rounded-lg border-none bg-primary px-10 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
              type="submit"
              disabled={loading || !newPassword || !confirmPassword}
            >
              {loading ? (
                <Trans>Enregistrement...</Trans>
              ) : (
                <Trans>Enregistrer et se connecter</Trans>
              )}
            </button>
            <button
              className="inline-block rounded-lg border border-primary border-opacity-30 px-6 py-2 text-primary disabled:opacity-50"
              type="button"
              onClick={() => {
                setError("");
                setStep("reset-otp");
              }}
              disabled={loading}
            >
              <Trans>Modifier le code</Trans>
            </button>
          </div>
        </form>
        {feedback}
      </>
    );
  }

  // Step 1b: the emailed verification code (accounts without a password).
  return (
    <>
      <h3 className="mb-2 text-lg font-semibold text-slate-900">
        <Trans>Code de vérification</Trans>
      </h3>
      <p className="text-slate-600">
        <Trans>Veuillez saisir le code envoyé à</Trans>{" "}
        <b className="text-primary">{email}</b>
      </p>
      <div className="-mx-4 mt-12 flex w-screen items-start justify-start xs:mx-0 xs:w-full ">
        <div className="w-full max-w-[500px] overflow-hidden [&>div]:w-full [&>div]:justify-start">
          {renderOtpBoxes(otp, setOtp)}
        </div>
      </div>
      <button
        className="mr-auto mt-5 inline-block rounded-lg border-none bg-primary px-10 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
        type="submit"
        onClick={() => {
          if (otp.length < 6) {
            setError(t`Veuillez entrer le code complet`);
            return;
          }
          onSubmit(otp);
        }}
        disabled={loading}
      >
        {loading ? <Trans>Connexion...</Trans> : <Trans>Se connecter</Trans>}
      </button>
      {feedback}
      <p className="mt-12 text-sm font-semibold italic text-slate-600">
        <Trans>
          Vérifiez votre dossier de spam si vous ne trouvez pas l'email, ça peut
          arriver...
        </Trans>
      </p>
    </>
  );
}
